import HeroAgentOrbit from "@/components/site/HeroAgentOrbit";

// Preview page: 4 hero-visual concepts for "traditional app -> agentic app".
// On-brand palette. Vector SVG mockups (representative, not final).
const INK = "#3E2818";
const CREAM = "#F7F5F0";
const TERRA = "#D1633D";
const AMBER = "#E0902B";
const GREY = "#B8B0A4";
const GREYBG = "#ECE9E3";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const SANS = "'IBM Plex Sans', system-ui, sans-serif";

/* ---------- A. Transformation Bridge (before -> after) ---------- */
const ConceptA = () => (
  <svg viewBox="0 0 520 400" className="w-full h-auto">
    <text x="16" y="24" fontFamily={MONO} fontSize="10" letterSpacing="1.2" fill={GREY}>TRADITIONAL APP</text>
    {/* legacy app card (muted) */}
    <rect x="16" y="40" width="180" height="300" rx="12" fill={GREYBG} stroke={GREY} strokeWidth="1" />
    <rect x="32" y="60" width="120" height="12" rx="3" fill={GREY} />
    <rect x="32" y="92" width="148" height="40" rx="6" fill="#fff" stroke={GREY} />
    <rect x="32" y="142" width="148" height="40" rx="6" fill="#fff" stroke={GREY} />
    <rect x="32" y="192" width="148" height="40" rx="6" fill="#fff" stroke={GREY} />
    <rect x="32" y="250" width="72" height="26" rx="13" fill={GREY} />
    <text x="68" y="267" textAnchor="middle" fontFamily={MONO} fontSize="9" fill="#fff">MANUAL</text>
    <text x="32" y="312" fontFamily={MONO} fontSize="9" fill={GREY}>static · form-driven</text>

    {/* bridge with agents */}
    <g>
      <line x1="196" y1="190" x2="324" y2="190" stroke={TERRA} strokeWidth="1.5" strokeDasharray="4 4" />
      <circle cx="230" cy="150" r="16" fill={CREAM} stroke={TERRA} strokeWidth="1.5" />
      <text x="230" y="155" textAnchor="middle" fontFamily={MONO} fontSize="13" fill={TERRA}>&#945;</text>
      <circle cx="260" cy="230" r="16" fill={CREAM} stroke={AMBER} strokeWidth="1.5" />
      <text x="260" y="235" textAnchor="middle" fontFamily={MONO} fontSize="13" fill={AMBER}>&#945;</text>
      <circle cx="290" cy="150" r="16" fill={CREAM} stroke={TERRA} strokeWidth="1.5" />
      <text x="290" y="155" textAnchor="middle" fontFamily={MONO} fontSize="13" fill={TERRA}>&#945;</text>
      <text x="260" y="285" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={TERRA}>AI AGENTS</text>
      <path d="M300 190 l14 0 m-6 -5 l6 5 l-6 5" stroke={TERRA} strokeWidth="1.5" fill="none" />
    </g>

    {/* agentic app card (vivid) */}
    <text x="504" y="24" textAnchor="end" fontFamily={MONO} fontSize="10" letterSpacing="1.2" fill={TERRA}>AGENTIC APP</text>
    <rect x="324" y="40" width="180" height="300" rx="12" fill="#fff" stroke={TERRA} strokeWidth="1.5" />
    <rect x="340" y="60" width="110" height="12" rx="3" fill={INK} />
    <rect x="340" y="92" width="148" height="46" rx="8" fill="#FCEFE8" stroke={TERRA} />
    <circle cx="356" cy="115" r="7" fill={TERRA} /><text x="356" y="119" textAnchor="middle" fontFamily={MONO} fontSize="9" fill="#fff">&#945;</text>
    <rect x="372" y="106" width="80" height="7" rx="3" fill={INK} opacity="0.7" />
    <rect x="372" y="120" width="60" height="6" rx="3" fill={GREY} />
    <rect x="340" y="148" width="148" height="46" rx="8" fill="#FBF2E1" stroke={AMBER} />
    <circle cx="356" cy="171" r="7" fill={AMBER} /><text x="356" y="175" textAnchor="middle" fontFamily={MONO} fontSize="9" fill="#fff">&#945;</text>
    <rect x="372" y="162" width="80" height="7" rx="3" fill={INK} opacity="0.7" />
    <rect x="372" y="176" width="52" height="6" rx="3" fill={GREY} />
    <rect x="340" y="204" width="148" height="46" rx="8" fill="#EAF0E2" stroke="#7FA55E" />
    <circle cx="356" cy="227" r="7" fill="#7FA55E" /><text x="356" y="231" textAnchor="middle" fontFamily={MONO} fontSize="9" fill="#fff">&#10003;</text>
    <rect x="372" y="218" width="80" height="7" rx="3" fill={INK} opacity="0.7" />
    <rect x="372" y="232" width="64" height="6" rx="3" fill={GREY} />
    <rect x="340" y="262" width="96" height="26" rx="13" fill={TERRA} />
    <text x="388" y="279" textAnchor="middle" fontFamily={MONO} fontSize="9" fill="#fff">AUTONOMOUS</text>
    <text x="340" y="312" fontFamily={MONO} fontSize="9" fill={TERRA}>self-running · goal-driven</text>
  </svg>
);

/* ---------- B. App wrapped by orbiting agents ---------- */
const ConceptB = () => {
  const agents = [
    { a: -90, l: "Validate", c: TERRA },
    { a: -30, l: "Approve", c: AMBER },
    { a: 30, l: "Post", c: "#4E6E9E" },
    { a: 90, l: "Notify", c: "#7FA55E" },
    { a: 150, l: "Reconcile", c: TERRA },
    { a: 210, l: "Audit", c: AMBER },
  ];
  const cx = 260, cy = 200, r = 150;
  return (
    <svg viewBox="0 0 520 400" className="w-full h-auto">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={GREY} strokeWidth="1" strokeDasharray="3 5" />
      {/* central app window */}
      <rect x={cx - 78} y={cy - 62} width="156" height="124" rx="12" fill="#fff" stroke={INK} strokeOpacity="0.15" strokeWidth="1" />
      <rect x={cx - 78} y={cy - 62} width="156" height="26" rx="12" fill={GREYBG} />
      <circle cx={cx - 64} cy={cy - 49} r="3" fill={GREY} /><circle cx={cx - 54} cy={cy - 49} r="3" fill={GREY} />
      <text x={cx} y={cy - 45} textAnchor="middle" fontFamily={MONO} fontSize="9" fill={INK}>BUSINESS APP</text>
      <rect x={cx - 62} y={cy - 24} width="124" height="10" rx="3" fill={GREYBG} />
      <rect x={cx - 62} y={cy - 6} width="90" height="8" rx="3" fill={GREYBG} />
      <rect x={cx - 62} y={cy + 10} width="110" height="8" rx="3" fill={GREYBG} />
      <rect x={cx - 62} y={cy + 30} width="60" height="18" rx="9" fill={TERRA} opacity="0.9" />
      {agents.map((ag, i) => {
        const rad = (ag.a * Math.PI) / 180;
        const ax = cx + r * Math.cos(rad), ay = cy + r * Math.sin(rad);
        return (
          <g key={i}>
            <line x1={cx + 82 * Math.cos(rad)} y1={cy + 66 * Math.sin(rad)} x2={ax} y2={ay} stroke={ag.c} strokeWidth="1.2" opacity="0.6" />
            <circle cx={ax} cy={ay} r="19" fill={CREAM} stroke={ag.c} strokeWidth="1.5" />
            <text x={ax} y={ay - 1} textAnchor="middle" fontFamily={MONO} fontSize="14" fill={ag.c}>&#945;</text>
            <text x={ax} y={ay + 11} textAnchor="middle" fontFamily={MONO} fontSize="6.5" fill={ag.c}>{ag.l}</text>
          </g>
        );
      })}
      <text x={cx} y="34" textAnchor="middle" fontFamily={MONO} fontSize="10" letterSpacing="1.2" fill={TERRA}>AGENTS OPERATE YOUR APP</text>
    </svg>
  );
};

/* ---------- C. Layered ascension ---------- */
const ConceptC = () => (
  <svg viewBox="0 0 520 400" className="w-full h-auto">
    {/* bottom: legacy */}
    <g transform="translate(90 250)">
      <path d="M0 40 L200 40 L280 0 L80 0 Z" fill={GREYBG} stroke={GREY} />
      <text x="140" y="26" textAnchor="middle" fontFamily={MONO} fontSize="10" fill={GREY} letterSpacing="1">LEGACY APPLICATION</text>
    </g>
    {/* middle: semantic/context */}
    <g transform="translate(110 170)">
      <path d="M0 40 L200 40 L280 0 L80 0 Z" fill="#FBF2E1" stroke={AMBER} />
      <text x="140" y="26" textAnchor="middle" fontFamily={MONO} fontSize="10" fill="#8A5730" letterSpacing="1">SEMANTIC + CONTEXT</text>
    </g>
    {/* top: agentic */}
    <g transform="translate(130 90)">
      <path d="M0 40 L200 40 L280 0 L80 0 Z" fill="#FCEFE8" stroke={TERRA} strokeWidth="1.5" />
      <text x="150" y="26" textAnchor="middle" fontFamily={MONO} fontSize="10" fill={TERRA} letterSpacing="1">AGENTIC LAYER</text>
    </g>
    {/* agents on top */}
    <circle cx="210" cy="70" r="15" fill={CREAM} stroke={TERRA} strokeWidth="1.5" /><text x="210" y="75" textAnchor="middle" fontFamily={MONO} fontSize="13" fill={TERRA}>&#945;</text>
    <circle cx="270" cy="55" r="13" fill={CREAM} stroke={AMBER} strokeWidth="1.5" /><text x="270" y="60" textAnchor="middle" fontFamily={MONO} fontSize="11" fill={AMBER}>&#945;</text>
    <circle cx="330" cy="72" r="12" fill={CREAM} stroke={TERRA} strokeWidth="1.5" /><text x="330" y="76" textAnchor="middle" fontFamily={MONO} fontSize="10" fill={TERRA}>&#945;</text>
    {/* upward arrow */}
    <path d="M60 300 C 20 220, 20 150, 60 110" stroke={TERRA} strokeWidth="1.5" fill="none" strokeDasharray="4 4" />
    <path d="M60 110 l-6 12 m6 -12 l6 12" stroke={TERRA} strokeWidth="1.5" fill="none" />
    <text x="26" y="210" fontFamily={MONO} fontSize="9" fill={TERRA} transform="rotate(-90 26 210)" letterSpacing="1.5">TRANSFORM</text>
  </svg>
);

/* ---------- D. Pipeline metamorphosis ---------- */
const ConceptD = () => (
  <svg viewBox="0 0 520 400" className="w-full h-auto">
    <text x="16" y="24" fontFamily={MONO} fontSize="10" letterSpacing="1" fill={GREY}>RIGID PROCESS</text>
    <text x="504" y="24" textAnchor="end" fontFamily={MONO} fontSize="10" letterSpacing="1" fill={TERRA}>AGENTIC WORKFLOW</text>

    {/* input: static form */}
    <rect x="16" y="140" width="120" height="150" rx="10" fill={GREYBG} stroke={GREY} />
    <rect x="32" y="158" width="88" height="9" rx="3" fill={GREY} />
    <rect x="32" y="180" width="88" height="20" rx="4" fill="#fff" stroke={GREY} />
    <rect x="32" y="210" width="88" height="20" rx="4" fill="#fff" stroke={GREY} />
    <rect x="32" y="240" width="88" height="20" rx="4" fill="#fff" stroke={GREY} />

    {/* transformer node cluster */}
    <g>
      <path d="M136 215 L196 215" stroke={GREY} strokeWidth="1.5" />
      <rect x="196" y="150" width="128" height="130" rx="14" fill={CREAM} stroke={TERRA} strokeWidth="1.5" strokeDasharray="5 4" />
      <text x="260" y="172" textAnchor="middle" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={TERRA}>AI AGENT TRANSFORMER</text>
      <circle cx="230" cy="210" r="15" fill="#fff" stroke={TERRA} strokeWidth="1.5" /><text x="230" y="215" textAnchor="middle" fontFamily={MONO} fontSize="13" fill={TERRA}>&#945;</text>
      <circle cx="290" cy="200" r="13" fill="#fff" stroke={AMBER} strokeWidth="1.5" /><text x="290" y="205" textAnchor="middle" fontFamily={MONO} fontSize="11" fill={AMBER}>&#945;</text>
      <circle cx="262" cy="248" r="12" fill="#fff" stroke="#4E6E9E" strokeWidth="1.5" /><text x="262" y="252" textAnchor="middle" fontFamily={MONO} fontSize="10" fill="#4E6E9E">&#945;</text>
      <line x1="230" y1="210" x2="290" y2="200" stroke={GREY} strokeWidth="1" />
      <line x1="290" y1="200" x2="262" y2="248" stroke={GREY} strokeWidth="1" />
      <line x1="262" y1="248" x2="230" y2="210" stroke={GREY} strokeWidth="1" />
      <path d="M324 215 L384 215 m-8 -6 l8 6 l-8 6" stroke={TERRA} strokeWidth="1.5" fill="none" />
    </g>

    {/* output: agentic workflow card */}
    <rect x="384" y="130" width="120" height="170" rx="10" fill="#fff" stroke={TERRA} strokeWidth="1.5" />
    <text x="400" y="152" fontFamily={MONO} fontSize="8" fill={TERRA}>RUNNING &#8226; AUTONOMOUS</text>
    {[0, 1, 2].map((i) => (
      <g key={i} transform={`translate(400 ${164 + i * 40})`}>
        <circle cx="8" cy="12" r="7" fill={i === 2 ? "#7FA55E" : TERRA} />
        <text x="8" y="16" textAnchor="middle" fontFamily={MONO} fontSize="8" fill="#fff">{i === 2 ? "\u2713" : "\u03b1"}</text>
        <rect x="22" y="4" width="72" height="7" rx="3" fill={INK} opacity="0.7" />
        <rect x="22" y="16" width="50" height="6" rx="3" fill={GREY} />
      </g>
    ))}
  </svg>
);

const CARDS = [
  { key: "A", name: "Transformation Bridge — Before / After", el: <ConceptA />, note: "Muted legacy app on the left transforms — via AI agents — into a vivid, autonomous agentic app on the right." },
  { key: "B", name: "App Wrapped by Agents (Orbit)", el: <ConceptB />, note: "Your existing business app at the center, ringed by AI agents that operate it (validate, approve, post, notify…)." },
  { key: "C", name: "Layered Ascension", el: <ConceptC />, note: "Legacy app is lifted through a semantic/context layer into an autonomous agentic layer. Ties to the Pillars story." },
  { key: "D", name: "Pipeline Metamorphosis", el: <ConceptD />, note: "A rigid process enters, passes through an AI-agent transformer, and emerges as a self-running agentic workflow." },
];

export default function HeroVisualPreview() {
  return (
    <div className="min-h-screen bg-[#E7E1D6] py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-display text-3xl font-semibold text-[#3E2818] mb-2">Hero Visual — Concepts</h2>
        <p className="text-[#6B5A4B] mb-8 max-w-2xl">Traditional business application → agentic application, powered by AI agents. Representative mockups in the brand palette (final build will be refined + optionally animated).</p>

        {/* Enhanced Option B */}
        <div className="rounded-2xl bg-[#F7F5F0] border border-[#D1633D]/30 p-6 shadow-sm mb-10">
          <p className="font-mono text-[11px] tracking-widest uppercase text-[#D1633D] mb-1">Option B — Enhanced (animated)</p>
          <h3 className="font-display text-xl font-semibold text-[#3E2818] mb-4">App Operated by AI Agents</h3>
          <div className="rounded-xl bg-[#F7F5F0] border border-[#3E2818]/8 p-4 max-w-2xl mx-auto">
            <HeroAgentOrbit className="w-full h-auto" />
          </div>
          <p className="text-sm text-[#3E2818]/70 leading-relaxed mt-4 max-w-2xl">Elevated central "Accounts Payable" app (floating, live status), six premium agent cards on a slowly rotating orbit, hub-and-spoke connectors with animated flow dots (data in / actions out), and an outer "Agentic Layer" arc. This is the direction — refinements welcome.</p>
        </div>
        <div className="border-t border-[#3E2818]/10 mb-8" />
        <p className="font-mono text-[11px] tracking-widest uppercase text-[#8C6A3F] mb-4">Original naive concepts (for reference)</p>
        <div className="grid md:grid-cols-2 gap-6">
          {CARDS.map((c) => (
            <div key={c.key} className="rounded-2xl bg-[#F7F5F0] border border-[#3E2818]/10 p-5 shadow-sm">
              <p className="font-mono text-[11px] tracking-widest uppercase text-[#D1633D] mb-1">Option {c.key}</p>
              <h3 className="font-display text-lg font-semibold text-[#3E2818] mb-3">{c.name}</h3>
              <div className="rounded-xl bg-[#F7F5F0] border border-[#3E2818]/8 p-2 mb-3">{c.el}</div>
              <p className="text-sm text-[#3E2818]/70 leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
